/**
 * "Billed by {sellerName}, a trade name of {sellerLegalName}" (MM-D-139, TMMAPP-0119 §A3): who bills
 * the person, beside the brand on every billing screen and host. With no distinct trade name it reads
 * "Billed by {sellerLegalName}"; with no seller served it renders nothing. body2 in `text2`; in a
 * dialog header it's a `DialogSubtitle`.
 */
import { Typography, useTheme, type SxProps, type Theme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { DialogSubtitle } from '@/components/DialogSubtitle';
import { resolveTokens } from '@/theme/theme';
import { useConsumerTerms } from './useConsumerTerms';

function useSellerLine(): string | null {
  const { t } = useTranslation();
  const who = useConsumerTerms().seller;
  if (who === null) return null;
  return who.name !== null
    ? t('billing.sellerLine', { name: who.name, legalName: who.legalName })
    : t('billing.sellerLineLegal', { legalName: who.legalName });
}

export function SellerLine({
  dialog = false,
  inAlert = false,
  sx,
}: {
  /** In a dialog header, under the title. */
  dialog?: boolean;
  /**
   * Inside an alert's message (the ENABLE AI prompt): a body2 line in the alert's own text color,
   * so it adds no new color pair on the alert's surface.
   */
  inAlert?: boolean;
  sx?: SxProps<Theme>;
}) {
  const tokens = resolveTokens(useTheme());
  const line = useSellerLine();
  if (line === null) return null;
  if (inAlert) {
    return (
      <Typography variant="body2" component="span" data-seller-line="" sx={{ display: 'block' }}>
        {line}
      </Typography>
    );
  }
  if (dialog) {
    return (
      <DialogSubtitle>
        <span data-seller-line="">{line}</span>
      </DialogSubtitle>
    );
  }
  return (
    <Typography
      variant="body2"
      component="p"
      data-seller-line=""
      sx={[{ color: tokens.text2 }, ...(sx === undefined ? [] : Array.isArray(sx) ? sx : [sx])]}
    >
      {line}
    </Typography>
  );
}
